import {
  Swimlane as RMSwimlane,
  useSwimlaneApiRef,
  useSwimlaneEventHandler,
} from '@/components/ui/swimlane';
import { TITLE_HEIGHT_TO_LINES } from '../swimlane/utils/constants';
import { SWIMLANE_LIMITS } from './constants';
import { NO_OP_ACTIONS, ReadOnlyColumnHeader, ReadOnlyRowHeader } from './shared/swimlane-headers';
import type { PublishedSwimlaneResponse } from './types';

interface PublishedSwimlaneProps {
  /** Pre-fetched swimlane data from parent - avoids duplicate API calls */
  data: PublishedSwimlaneResponse;
  isPngPublicationTemplate?: boolean;
  onItemClick?: (itemId: string) => void;
  /** Enable transparent background for PNG export */
  transparentBackground?: boolean;
}

export function PublishedSwimlane({
  data,
  isPngPublicationTemplate = false,
  onItemClick,
  transparentBackground = false,
}: PublishedSwimlaneProps) {
  const apiRef = useSwimlaneApiRef();

  // Subscribe to openCardDetails event to handle item clicks
  useSwimlaneEventHandler(apiRef, 'openCardDetails', (params) => {
    if (onItemClick && params) {
      onItemClick(params.itemID);
    }
  });

  const items = new Map(Object.entries(data.items));

  const cells = new Map(
    Object.entries(data.cells).map(([rowId, cols]) => [rowId, new Map(Object.entries(cols))]),
  );

  const layout = data.layout;
  const rowHeaderOrientation = layout.headerDirection;
  const isCompact = layout.theme === 'compact';

  const backgroundColor = 'var(--export-bg-color, white)';

  return (
    <div className="h-full w-full px-4 py-2" data-transparent-background={transparentBackground}>
      <RMSwimlane
        isPublicationMode={true}
        readOnly={true}
        actions={NO_OP_ACTIONS}
        showExpandedSubItems={true}
        columnHeaders={data.columnHeaders}
        rowHeaders={data.rowHeaders}
        items={items}
        cells={cells}
        emptyState={null}
        isAddColumnHeaderButtonVisible={false}
        isAddRowHeaderButtonVisible={false}
        isCompact={isCompact}
        isHeadersSticky
        isItemDateVisible={data.layout.showItemDates ?? true}
        isItemDescriptionVisible={false}
        isItemDetailsVisible={true}
        itemLabelSize={layout.titleSize ?? 'medium'}
        maxCardTitleLines={TITLE_HEIGHT_TO_LINES[layout.titleHeight ?? 'default']}
        rowHeaderOrientation={rowHeaderOrientation}
        rowHeadersWidth={SWIMLANE_LIMITS.rowHeadersWidth}
        showGridLines={layout.showGridLines ?? true}
        apiRef={apiRef}
        maxSelectedCards={SWIMLANE_LIMITS.maxSelectedCards}
        maxSubItemsPerItem={SWIMLANE_LIMITS.maxSubItemsPerItem}
        isSubItemFeaturesEnabled={true}
        columnAxisLabelText={data.columnAxisLabel}
        rowAxisLabelText={data.rowAxisLabel || null}
        isPngPublicationTemplate={isPngPublicationTemplate}
        backgroundColor={backgroundColor}
        slots={{
          columnHeader: ReadOnlyColumnHeader,
          rowHeader: ReadOnlyRowHeader,
        }}
      />
    </div>
  );
}
